/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef, useMemo, memo } from 'react';
import { Box, Icon, Flex, Avatar, Tooltip, Spinner } from '@chakra-ui/react';
import { components } from 'react-select';

import moment from 'moment-timezone';
import AsyncSelect from 'react-select/async';
import { useDispatch } from 'react-redux';
import { useField } from 'formik';
import debounce from 'lodash.debounce';
import { AiLoader, Star } from '@/assets/icons/ai';
import { Plus } from '@kula-ai/icons/outline/Plus';
import { CalendarCross } from '@/assets/icons/ats';
import { getCustomStyles } from '@/theme/components/select';
import { fetchInterviewersList } from '@/store/atsScheduling/actions';
import { FeaturedIcon } from '@/assets/icons/ats';

import './index.css';
import { KText } from '@/ui-library/components/text';

const MemoizedAvatar = memo(({ src, name }) => (
  <Avatar
    src={src}
    name={name}
    width="16px"
    height="16px"
    borderRadius="6px"
    className="avatar-xs"
  />
));

const AddInterviewersField = ({
  onChange,
  actionId,
  startDate,
  endDate,
  isLoading,
  variant,
  suggestedInterviewer,
  userInfo,
  IsNoSlotsAvailable = false,
}) => {
  const [_field, meta, helpers] = useField('participant_ids'); // eslint-disable-line
  const { value: interviewType } = useField('interview_type')[1]; // eslint-disable-line
  const { setValue, setTouched, setError } = helpers;
  const [showSuggestedInterviewer, setShowSuggestedInterviewer] =
    useState(false);
  const [values, setValues] = useState([]);
  const [options, setOptions] = useState([]);
  const selectRef = useRef(null);
  const { initialValue, value, error, touched } = meta;
  const isError = touched && error && !values.length;
  const dispatch = useDispatch();

  const noSlotsAvailable = useMemo(() => {
    if (IsNoSlotsAvailable && value.length) {
      if (value.length === 1) {
        return {
          type: 'single',
          name: value[0].name,
        };
      } else {
        return {
          type: 'group',
          name: 'Interviewers',
        };
      }
    }

    return false;
  }, [IsNoSlotsAvailable, value]);

  const styles = useMemo(
    () => [
      {
        type: 'control',
        newStyles: {
          boxShadow: isError ? '0 0 0 2px #A32020' : 'none',
        },
      },
      {
        type: 'multiValue',
        newStyles: {
          fontSize: '12px',
          fontWeight: '500',
          border: 'none',
          borderRadius: '6px',
          background: '#fff',
        },
      },
      {
        type: 'multiValueRemove',
        newStyles: {
          '&:hover': {
            background: '#fff',
          },
        },
      },
    ],
    [isError]
  );

  const setFieldProps = async (selectedOption) => {
    if (variant === 'manual' && interviewType === 'one_on_one') {
      const interviewer = selectedOption.pop();
      const interviewerList = interviewer ? [interviewer] : [];
      onChange(interviewerList);
      setValues(interviewerList);
    } else {
      onChange(selectedOption);
      setValues(selectedOption);
    }
    setTouched(true);
    setError(null);
  };

  useEffect(() => {
    setValue(values);
  }, [values]);

  useEffect(() => {
    if (!value.length) {
      setFieldProps(initialValue);
    }
  }, [initialValue]);

  useEffect(() => {
    if (isLoading) {
      setShowSuggestedInterviewer(true);
    }
  }, [isLoading]);

  const getFormattedOptions = (options) => {
    return [
      {
        label: 'selected interviewers',
        options: options?.pool_interviewers?.map((user) => ({
          ...user,
          value: user.id,
          label: user.name,
        })),
      },
      {
        label: 'all interviewers',
        options: options?.interviewers?.map((user) => ({
          ...user,
          value: user.id,
          label: user.name,
        })),
      },
    ];
  };

  const loadOptions = (inputValue, callback) => {
    try {
      dispatch(
        fetchInterviewersList({
          inputValue,
          actionId,
          startDate,
          cb: async (interviewers) => {
            const res = await interviewers;
            callback(getFormattedOptions(res?.data));
          },
        })
      );
    } catch (e) {
      callback([]);
    }
  };

  const debouncedLoadOptions = debounce(loadOptions, 300);

  const handleSelectInterviewer = () => {
    setFieldProps([suggestedInterviewer]);
  };

  useEffect(() => {
    triggerLoadOptions();
  }, [startDate]);

  const triggerLoadOptions = () => {
    if (selectRef.current) {
      selectRef.current.loadOptions('', (newOptions) => setOptions(newOptions));
    }
  };

  return (
    <Flex flexDir="column">
      <Flex flexDir="column" w="100%" margin="0.75em 0">
        <Flex>
          <KText size="sm-medium" color="secondary-900" mb="8px">
            Add interviewer
          </KText>
          <KText color="red.500" mt="-2px" ml="4px">
            *
          </KText>
        </Flex>
        {startDate && (
          <AsyncSelect
            ref={selectRef}
            defaultOptions={options}
            loadOptions={debouncedLoadOptions}
            loadingMessage={() => (
              <KText>
                <Spinner color="primary-800" size="md" />
              </KText>
            )}
            value={values}
            isMulti
            isClearable={false}
            styles={getCustomStyles([
              ...styles,
              {
                type: 'control',
                boxShadow: isError ? '0 0 0 2px #A32020' : 'none',
              },
            ])}
            boxShadow={isError && '0 0 0 2px #A32020'}
            components={{
              DropdownIndicator: null,
              IndicatorSeparator: null,
              Menu: (props) => (
                <CustomMenu
                  {...props}
                  startDate={startDate}
                  endDate={endDate}
                />
              ),
              MenuList: CustomMenuList,
              Option: CustomOptions,
              GroupHeading: CustomGroupHeading,
              MultiValueContainer: CustomMultiValueContainer,
              MultiValueLabel: (props) =>
                CustomMultiValueLabel(props, userInfo),
            }}
            onChange={setFieldProps}
            onClick={() => setTouched(true)}
          />
        )}
        {isError && (
          <KText color="ats-negative" size="sm" mt="8px">
            {error}
          </KText>
        )}
      </Flex>
      {showSuggestedInterviewer && variant === 'manual' && (
        <>
          {isLoading && (
            <Flex
              bg="#605AFB0F"
              minH="100px"
              gap="12px"
              borderRadius="6px"
              flexDir="column"
              justifyContent="center"
              alignItems="center"
              py="10px"
            >
              <Icon as={AiLoader} w="40px" h="40px" />
              <KText size="sm-regular">
                Looking for the best interviewer...
              </KText>
            </Flex>
          )}
          {!isLoading &&
            (suggestedInterviewer !== null ? (
              <Flex
                bg="#605AFB0F"
                minH="100px"
                gap="12px"
                borderRadius="6px"
                flexDir="column"
                justifyContent="center"
                alignItems="center"
                p="10px"
              >
                <KText size="sm-regular">
                  Recommended interviewer for this interview:{' '}
                </KText>
                <Flex
                  gap="5px"
                  padding="5px 8px"
                  alignItems="center"
                  border="1px solid #cdcdcd"
                  bg="#FFF"
                  cursor="pointer"
                  borderRadius="6px"
                  onClick={handleSelectInterviewer}
                >
                  <MemoizedAvatar
                    src={suggestedInterviewer?.avatar}
                    name={suggestedInterviewer?.label}
                  />
                  <KText size="sm-medium">{suggestedInterviewer?.label}</KText>
                  {suggestedInterviewer?.isAiRecommendation && (
                    <>
                      <Icon as={Plus} w="16px" h="16px" />
                    </>
                  )}
                </Flex>
              </Flex>
            ) : (
              <Flex bg="#F7F7F7" borderRadius="6px" p="16px 14px" minH="100px">
                <KText>
                  <KText size="sm-semi-bold" color="secondary-900">
                    Note:
                  </KText>{' '}
                  We couldn't recommend the ideal interviewer from the
                  interviewers configured in the interview plan based on their
                  working hours, calendar availability, and weekly interviewing
                  hours. Please add an interviewer manually.
                </KText>
              </Flex>
            ))}
        </>
      )}
      {variant !== 'manual' && noSlotsAvailable && (
        <Flex
          bg="#FFF2CC"
          border="1px solid #EAEAEA"
          borderRadius="6px"
          p="10px"
          flexDirection="row"
        >
          <Icon as={FeaturedIcon} w="45px" h="auto" />
          <Flex flexDir="column" gap="5px">
            <KText size="sm-medium" color="secondary-900">
              {noSlotsAvailable?.type === 'single'
                ? `${noSlotsAvailable?.name} is`
                : 'Interviewers are'}{' '}
              not available in the selected date range
            </KText>
            <KText c size="sm-regular" color="secondary-700">
              Choose{' '}
              {noSlotsAvailable?.type === 'single'
                ? `another interviewer`
                : 'other interviewers'}
              , modify your date range or select slots manually
            </KText>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

const CustomGroupHeading = (props) => {
  return (
    <Flex padding="0px 15px 0px 15px">
      <KText
        {...props}
        size="md-semi-bold"
        color="#413B74"
        textTransform="uppercase"
      />
    </Flex>
  );
};

const CustomMenu = (props) => {
  return (
    <components.Menu {...props}>
      <Flex padding="10px 15px 0px 15px" justifyContent="space-between">
        <KText size="sm-medium" color="secondary-700">
          Availability
        </KText>
        <KText size="sm-medium" color="secondary-700">
          {moment.parseZone(props.startDate).format('D MMM')} to{' '}
          {moment.parseZone(props.endDate).subtract(1, 'days').format('D MMM')}
        </KText>
      </Flex>
      {props.children}
    </components.Menu>
  );
};

// Custom MenuList component
const CustomMenuList = (props) => {
  return <components.MenuList {...props}>{props.children}</components.MenuList>;
};

const CustomOptions = ({ innerProps, data }) => {
  const { avatar, label, week_interview_hours, week_interview_hours_limit } =
    data || {};
  return (
    <Flex
      {...innerProps}
      padding="10px 10px"
      mx="6px"
      alignItems="center"
      justifyContent="space-between"
      _hover={{ bg: '#F4F4F5', borderRadius: '5px', cursor: 'pointer' }}
    >
      <Flex gap="5px" alignItems={'center'}>
        <MemoizedAvatar src={avatar} name={label} />
        <KText size="sm-medium">{label}</KText>
      </Flex>
      <KText size="sm-medium">
        {week_interview_hours || 0}{' '}
        {week_interview_hours_limit && `/ ${week_interview_hours_limit || 0}`}{' '}
        hrs
      </KText>
    </Flex>
  );
};

const CustomMultiValueContainer = (props) => {
  return (
    <Box
      className="interviewers-multi-content"
      style={{
        display: 'flex',
        alignItems: 'center',
        border: '1px solid #ccc',
        borderRadius: '5px',
        margin: '2.5px',
      }}
    >
      <components.MultiValueContainer {...props} />
    </Box>
  );
};

const CustomMultiValueLabel = ({ innerProps, data }, userInfo) => {
  const activeUser = userInfo?.find((user) => user.id === data.id);
  return (
    <components.MultiValueLabel {...innerProps}>
      <Flex gap="5px" padding="5px 8px" alignItems="center">
        <MemoizedAvatar src={userInfo && data.avatar} name={data.label} />
        <KText size="sm-medium">{data.label}</KText>
        {activeUser?.calendar_available === false && (
          <Tooltip
            label="This user hasn’t connected their calendar yet."
            arrowSize={8}
            hasArrow
            placement="top"
            borderRadius="4px"
          >
            <Flex
              alignItems="center"
              justifyContent="center"
              ml="5px"
              mt="2px"
              mr="-5px"
            >
              <Icon
                as={CalendarCross}
                w="20px"
                h="20px"
                className="primary-900"
              />
            </Flex>
          </Tooltip>
        )}
        {data.isAiRecommendation && (
          <>
            <Icon as={Star} w="16px" h="16px" color="primary-700" />
            <KText size="sm-semi-bold" color="primary-700">
              Recommended
            </KText>
          </>
        )}
      </Flex>
    </components.MultiValueLabel>
  );
};

export default memo(AddInterviewersField);
